import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";

const Pill = ({ children }: { children: React.ReactNode }) => (
  <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">{children}</span>
);

const ubicacionTexto: Record<string, string> = {
  domicilio: "A domicilio",
  consultorio: "En consultorio",
  ambos: "A domicilio y en consultorio",
};

const TherapistProfile = () => {
  const { id } = useParams<{ id: string }>();

  // Datos publicos via RPC. La tabla profiles no es accesible sin sesion.
  const { data, isLoading, error } = useQuery({
    queryKey: ["public-therapist", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_therapist", { p_id: id as string });
      if (error) throw error;
      return (data ?? [])[0] ?? null;
    },
  });

  const notFound = !isLoading && !error && !data;

  // Registro silencioso de visita al perfil: una sola llamada por id y montaje.
  // El RPC devuelve void (204 sin cuerpo). No bloquea ni muestra nada al usuario.
  const registeredViewId = useRef<string | null>(null);
  useEffect(() => {
    if (!data?.id) return;
    if (registeredViewId.current === data.id) return;
    registeredViewId.current = data.id;
    (async () => {
      try {
        await supabase.rpc("register_therapist_view", { p_id: data.id });
      } catch (err) {
        console.debug("register_therapist_view falló", err);
      }
    })();
  }, [data?.id]);

  const displayName = data
    ? data.es_centro_masajes && data.nombre_centro
      ? data.nombre_centro
      : [data.nombre, data.apellidos].filter(Boolean).join(" ")
    : "";

  const jsonLd =
    data && displayName
      ? {
          "@context": "https://schema.org",
          "@type": data.es_centro_masajes ? "LocalBusiness" : "Person",
          name: displayName,
          url: `https://masshappy.com/terapeutas/${id ?? ""}`,
          ...(data.foto_portada ? { image: data.foto_portada } : {}),
          address: {
            "@type": "PostalAddress",
            ...(data.ciudad ? { addressLocality: data.ciudad } : {}),
            addressCountry: "ES",
          },
          ...(data.ciudad ? { areaServed: data.ciudad } : {}),
          ...((data.total_resenas ?? 0) > 0
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: data.calificacion_promedio,
                  reviewCount: data.total_resenas,
                },
              }
            : {}),
        }
      : undefined;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={displayName ? `${displayName}${data?.ciudad ? ` · ${data.ciudad}` : ""}` : "Perfil de terapeuta"}
        description={
          displayName
            ? `Conoce a ${displayName}, terapeuta de bienestar${data?.ciudad ? ` en ${data.ciudad}` : ""}. Servicios, experiencia y valoraciones.`
            : "Perfil de terapeuta en MassHappy."
        }
        url={`https://www.masshappy.com/terapeutas/${id ?? ""}`}
        jsonLd={jsonLd}
      />




      <main className="container mx-auto max-w-4xl px-4 py-12 md:py-16">
        <Link
          to="/terapeutas"
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-primary transition-colors"
        >
          Volver a terapeutas
        </Link>

        {isLoading && (
          <div className="mt-8 animate-pulse space-y-6">
            <div className="aspect-[16/9] w-full rounded-xl bg-muted" />
            <div className="h-6 w-1/2 rounded bg-muted" />
            <div className="h-4 w-1/3 rounded bg-muted" />
            <div className="h-24 w-full rounded bg-muted" />
          </div>
        )}

        {!isLoading && error && (
          <p className="mt-10 text-sm text-muted-foreground">
            No hemos podido cargar este perfil. Inténtalo de nuevo en unos minutos.
          </p>
        )}

        {notFound && (
          <div className="mt-16 text-center">
            <h1 className="font-serif text-2xl md:text-3xl text-foreground">
              No hemos encontrado este perfil
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Puede que ya no esté publicado.
            </p>
            <div className="mt-6">
              <Button asChild>
                <Link to="/terapeutas">Ver todos los terapeutas</Link>
              </Button>
            </div>
          </div>
        )}

        {data && (
          <article className="mt-6">
            {data.is_demo && (
              <span className="inline-block rounded-md bg-foreground/80 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-background">
                Demostración
              </span>
            )}

            <div className="mt-4 overflow-hidden rounded-xl border border-border bg-muted">
              {data.foto_portada ? (
                <img
                  src={data.foto_portada}
                  alt={`Foto de ${displayName}`}
                  className="aspect-[16/9] w-full object-cover"
                  loading="eager"
                />
              ) : (
                <div className="flex aspect-[16/9] w-full items-center justify-center">
                  <span className="font-serif text-6xl text-muted-foreground">
                    {(displayName || "?").charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            <header className="mt-6">
              <h1 className="font-serif text-3xl md:text-4xl text-foreground">{displayName}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {[
                  data.ciudad,
                  data.anos_experiencia ? `${data.anos_experiencia} años de experiencia` : null,
                  data.ubicacion_servicio ? ubicacionTexto[data.ubicacion_servicio] : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {(data.total_resenas ?? 0) > 0 && (
                <p className="mt-1 text-sm text-muted-foreground">
                  {(data.calificacion_promedio ?? 0).toLocaleString("es", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}{" "}
                  · {data.total_resenas} reseñas
                </p>
              )}
            </header>

            {(data.bio || data.descripcion) && (
              <section className="mt-8 space-y-4">
                {data.bio && (
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{data.bio}</p>
                )}
                {data.descripcion && (
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {data.descripcion}
                  </p>
                )}
              </section>
            )}

            {(data.tipos_masaje?.length ?? 0) > 0 && (
              <section className="mt-8">
                <h2 className="font-serif text-xl text-foreground">Terapias</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {data.tipos_masaje!.map((tipo: string) => (
                    <Pill key={tipo}>{tipo.replace(/_/g, " ")}</Pill>
                  ))}
                </div>
              </section>
            )}

            {(data.idiomas?.length ?? 0) > 0 && (
              <section className="mt-8">
                <h2 className="font-serif text-xl text-foreground">Idiomas</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {data.idiomas!.map((idioma: string) => (
                    <Pill key={idioma}>{idioma}</Pill>
                  ))}
                </div>
              </section>
            )}

            {(data.certificaciones?.length ?? 0) > 0 && (
              <section className="mt-8">
                <h2 className="font-serif text-xl text-foreground">Certificaciones</h2>
                <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
                  {data.certificaciones!.map((cert: string) => (
                    <li key={cert}>{cert}</li>
                  ))}
                </ul>
              </section>
            )}

            {(data.precio_desde || data.precio_hasta) && (
              <section className="mt-8 rounded-xl border border-border bg-card p-5 shadow-soft">
                <h2 className="font-serif text-xl text-foreground">Precios</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {data.precio_desde ? `Desde ${data.precio_desde} €` : ""}
                  {data.precio_desde && data.precio_hasta ? " · " : ""}
                  {data.precio_hasta ? `Hasta ${data.precio_hasta} €` : ""}
                </p>
              </section>
            )}

            {(data.galeria_fotos?.length ?? 0) > 0 && (
              <section className="mt-8">
                <h2 className="font-serif text-xl text-foreground">Galería</h2>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {data.galeria_fotos!.map((foto: string, i: number) => (
                    <img
                      key={foto + i}
                      src={foto}
                      alt={`Foto ${i + 1} de ${displayName}`}
                      loading="lazy"
                      className="aspect-square w-full rounded-lg object-cover"
                    />
                  ))}
                </div>
              </section>
            )}

            <div className="mt-10">
              <Button
                size="lg"
                className="w-full sm:w-auto"
                onClick={() => toast("Muy pronto podrás contactar directamente desde aquí.")}
              >
                Contactar
              </Button>
            </div>
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default TherapistProfile;
